/**
 * OSD Cards Component
 *
 * Displays cards for selecting OSD operator on homepage
 * Shows 4 cards (TAURON, PGE, ENEA, ENERGA) with:
 * - OSD logo placeholder
 * - Display name
 * - Coverage regions
 * - Customer count
 * - Brand color
 *
 * Clicking a card navigates to /:osd/
 */

import { Card, SimpleGrid, Title, Text, Badge, Group, Stack, ThemeIcon, Box } from '@mantine/core';
import { IconMapPin, IconUsers } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { getAllOSDMetadata } from '../../../utils/osdMetadata';
import type { OSDMetadata } from '../../../utils/osdMetadata';

interface OSDCardsProps {
  /**
   * Optional title above OSD cards
   */
  title?: string;
}

/**
 * OSD Cards Component
 */
export function OSDCards({ title = 'Wybierz swojego operatora' }: OSDCardsProps) {
  const allOSD = getAllOSDMetadata();

  return (
    <Stack gap="xl">
      {title && (
        <Title order={2} ta="center">
          {title}
        </Title>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="lg">
        {allOSD.map((osd) => (
          <OSDCard key={osd.code} osd={osd} />
        ))}
      </SimpleGrid>
    </Stack>
  );
}

/**
 * OSD Card Component
 *
 * Individual card for a single OSD operator
 */
function OSDCard({ osd }: { osd: OSDMetadata }) {
  return (
    <Card
      component={Link}
      to={`/${osd.code.toLowerCase()}`}
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        transition: 'transform var(--ec-transition-normal, 200ms ease), box-shadow var(--ec-transition-normal, 200ms ease)',
        textDecoration: 'none',
        borderColor: osd.color,
        borderWidth: 2,
      }}
      styles={{
        root: {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 'var(--ec-shadow-hover, 0 4px 12px rgba(0, 0, 0, 0.12))',
          },
        },
      }}
    >
      <Stack gap="md">
        {/* OSD Logo/Icon Placeholder */}
        <ThemeIcon
          size={60}
          radius="md"
          variant="light"
          style={{ backgroundColor: `${osd.color}10` }}
        >
          <Box
            style={{
              width: 40,
              height: 40,
              backgroundColor: osd.color,
              borderRadius: '50%',
            }}
          />
        </ThemeIcon>

        {/* OSD Name */}
        <div>
          <Title order={3} size="h4" mb={4}>
            {osd.shortName}
          </Title>
          <Text size="xs" c="dimmed" lineClamp={2}>
            {osd.description}
          </Text>
        </div>

        {/* Customer Count */}
        {osd.customersCount && (
          <Group gap="xs">
            <IconUsers size={16} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              {(osd.customersCount / 1000000).toFixed(1)}M klientów
            </Text>
          </Group>
        )}

        {/* Coverage Badge */}
        <Group gap="xs">
          <IconMapPin size={16} color="var(--mantine-color-dimmed)" />
          <Badge variant="light" color="gray" size="sm">
            {osd.coverage.length} województw
          </Badge>
        </Group>

        {/* Coverage Regions (first 3) */}
        <Stack gap={4}>
          {osd.coverage.slice(0, 3).map((region, idx) => (
            <Text key={idx} size="xs" c="dimmed">
              • {region}
            </Text>
          ))}
          {osd.coverage.length > 3 && (
            <Text size="xs" c="dimmed" fw={500}>
              +{osd.coverage.length - 3} więcej
            </Text>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
